import { SelectOption } from "../components/PrevetMain"
import { PrevetSpeedGraphZoneType } from "../getSpeedChartOptionsHelpers"

export const INITIALIZE_TAB = "INITIALIZE_TAB"
export const NEW_TAB = "NEW_TAB"
export const DUPLICATE_SELECTED_TAB = "DUPLICATE_SELECTED_TAB"
export const REMOVE_SELECTED_TAB = "REMOVE_SELECTED_TAB"
export const RENAME_SELECTED_TAB = "RENAME_SELECTED_TAB"
export const SELECT_TAB = "SELECT_TAB"
export const UPDATE_TAB_STATE = "UDPATE_TAB_STATE"

export const DEFAULT_SPEED_DATE_RANGE = "6 Months"

export interface CirculatedVesselDescription {
  speed: string
  weight: string,
  seaDays: string
}

export interface PrevetState {
  id: number
  imo?: SelectOption
  date: string
  tabName: string
  deleted: boolean
  cvdLadenSpeed: string
  cvdLadenFuelCon: string
  cvdLadenSeaDays: string
  cvdBallastSpeed: string
  cvdBallastFuelCon: string
  cvdBallastSeaDays: string
  vesselSpeedGraphZoneType: PrevetSpeedGraphZoneType
  vesselSpeedDateRange: string
  perfExposureHireRate: string
  perfExposureFuelPrice: string
  perfExposureValue: string
  hvIncludeBallast: boolean
  hvIncludeLaden: boolean
  hvSearchText: string
  hvCurrentPage: number
  hvRowsPerPage: number
}

export interface PrevetTabState {
  tabStates: PrevetState[]
  sequenceIndex: number[]
}

export type PrevetStateTabActions =
  | {
    type: typeof INITIALIZE_TAB
  }
  | {
    type: typeof NEW_TAB
  } | {
    type: typeof DUPLICATE_SELECTED_TAB
    payload: number
  } | {
    type: typeof REMOVE_SELECTED_TAB
    payload: number
  } | {
    type: typeof RENAME_SELECTED_TAB
    payload: { selectedIndex: number, name: string }
  } | {
    type: typeof SELECT_TAB
    payload: number
  } | {
    type: typeof UPDATE_TAB_STATE
    payload: { selectedIndex: number, key: keyof PrevetState, value: SelectOption | PrevetSpeedGraphZoneType | string | boolean | number | undefined }
  }
export type PrevetTabs = PrevetTabState | undefined

export const GetCurrentDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(now.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export const InitialPrevetTabState: PrevetTabState = {
  tabStates: [{
    id: 0,
    imo: undefined,
    date: GetCurrentDate(),
    tabName: `Tab 1`,
    deleted: false,
    cvdLadenSpeed: "",
    cvdLadenFuelCon: "",
    cvdLadenSeaDays: "",
    cvdBallastSpeed: "",
    cvdBallastFuelCon: "",
    cvdBallastSeaDays: "",
    vesselSpeedGraphZoneType: PrevetSpeedGraphZoneType.Outliers,
    vesselSpeedDateRange: DEFAULT_SPEED_DATE_RANGE,
    perfExposureHireRate: "",
    perfExposureFuelPrice: "",
    perfExposureValue: "",
    hvIncludeBallast: true,
    hvIncludeLaden: true,
    hvSearchText: "",
    hvCurrentPage: 1,
    hvRowsPerPage: 10
  }],
  sequenceIndex: [0]
}

const updatePrevetTabStateById = (
  prevetTabStates: PrevetState[],
  idToUpdate: number,
  modifiedKeys: Partial<Record<keyof PrevetState, any>>
): PrevetState[] => {
  if (prevetTabStates) {
    const updatedPrevetTabState = prevetTabStates.map((prevetTabState, index) =>
      index === idToUpdate
        ? { ...prevetTabState, ...modifiedKeys }
        : prevetTabState
    )
    return updatedPrevetTabState
  }
  return prevetTabStates
}


export const PrevetStateReducer = (
  state: PrevetTabs,
  action: PrevetStateTabActions
): PrevetTabs => {
  switch (action.type) {
    case INITIALIZE_TAB: {
      return { tabStates: [...(InitialPrevetTabState.tabStates)], sequenceIndex: [...InitialPrevetTabState.sequenceIndex] }
    }
    case NEW_TAB: {
      if (state) {
        const lastIndex = ((state?.tabStates ?? []).length)
        state?.sequenceIndex.push(lastIndex)
        const exist = state.tabStates.find(i => i.tabName === `Tab ${lastIndex + 1}` && !i.deleted)
        let newTabName = `Tab ${lastIndex + 1}`
        if (exist) {
          newTabName = exist.tabName + " new"
        }
        return {
          tabStates: [...(state?.tabStates ?? []),
          {
            id: lastIndex,
            imo: undefined,
            date: GetCurrentDate(),
            tabName: newTabName,
            deleted: false,
            cvdLadenSpeed: "",
            cvdLadenFuelCon: "",
            cvdLadenSeaDays: "",
            cvdBallastSpeed: "",
            cvdBallastFuelCon: "",
            cvdBallastSeaDays: "",
            vesselSpeedGraphZoneType: PrevetSpeedGraphZoneType.Outliers,
            vesselSpeedDateRange: DEFAULT_SPEED_DATE_RANGE,
            perfExposureHireRate: "",
            perfExposureFuelPrice: "",
            perfExposureValue: "",
            hvIncludeBallast: true,
            hvIncludeLaden: true,
            hvSearchText: "",
            hvCurrentPage: 1,
            hvRowsPerPage: 10
          }],
          sequenceIndex: [...state?.sequenceIndex]
        }
      }
      return state

    }
    case DUPLICATE_SELECTED_TAB: {
      if (state) {
        const selectedTab = state.tabStates[action.payload]
        const newTabState = [...state?.tabStates]
        const lastIndex = ((newTabState ?? []).length)
        const copiedTabState = { ...selectedTab, tabName: `${selectedTab.tabName} copy`, id: lastIndex }
        newTabState.push(copiedTabState)
        state?.sequenceIndex.push(lastIndex)
        return {
          tabStates: [...newTabState],
          sequenceIndex: [...state?.sequenceIndex]
        }
      }
      return state
    }
    case REMOVE_SELECTED_TAB: {
      if (state) {
        state.tabStates[action.payload]["deleted"] = true
        return {
          ...state,
          sequenceIndex: [...state?.sequenceIndex.filter(i => i !== action.payload)]
        }
      }
      return state
    }
    case RENAME_SELECTED_TAB: {
      if (state) {
        state.tabStates[action.payload.selectedIndex]["tabName"] = action.payload.name
        return {
          ...state
        }
      }
      return state
    }
    case UPDATE_TAB_STATE: {
      if (state) {
        const modifiedPayload = { [action.payload.key]: action.payload.value }
        state.tabStates = updatePrevetTabStateById(state.tabStates, action.payload.selectedIndex, modifiedPayload)
        return {
          ...state
        }
      }
      return state
    }
    case SELECT_TAB: {
      if (state) {
        const newSeqIndex = state.sequenceIndex.filter(i =>
          i !== action.payload
          && state.tabStates.find(state => state.id === i && !state.deleted))
        newSeqIndex.push(action.payload)
        return {
          ...state,
          sequenceIndex: [...newSeqIndex]
        }
      }
      return state
    }

    default:
      return state
  }
}
